import React, { useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { useTheme } from "@emotion/react";
import { isMobile } from "react-device-detect";
import { enqueueAgentNotification } from "api/whatsapp";
import {
  GetSumPackagesForAgent,
  PatchPackageAgentWhatsappStatus,
} from "api/packages";
import Swal from "sweetalert2";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { DataGridCustomToolbarClientMode } from "components/DataGridCustomToolbar";
import {
  copyTextToClipboard,
  playAlertSound,
  playCorrectSound,
  playIncorrectSound,
} from "utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import SendIcon from "@mui/icons-material/Send";
import { user } from "localStorage";

const Agent = () => {
  const theme = useTheme();
  const [rows, setRows] = useState(null);
  // const [successCount, setSuccessCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [filteredRows, setFilteredRows] = useState(null);

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");

  const _user = user();

  function filterPackage(dataArr, searchValue) {
    if (dataArr) {
      let newArr = dataArr.slice();
      // if (searchValue) {
      //   newArr = newArr.filter((element) => {
      //     return (
      //       (element._id.customer.name &&
      //         element._id.customer.name
      //           .toLowerCase()
      //           .includes(searchValue.toLowerCase())) ||
      //       (element._id.customer.whatsapp &&
      //         element._id.customer.whatsapp
      //           .toLowerCase()
      //           .includes(searchValue.toLowerCase()))
      //     );
      //   });
      // }
      setFilteredRows(newArr);
    }
  }

  function deleteRow(dataArr, rowId) {
    if (dataArr) {
      let newArr = dataArr.slice();
      if (rowId) {
        newArr = newArr.filter((element) => {
          return element.agent.id + element._id.station !== rowId;
        });
      }
      setFilteredRows(newArr);
    }
  }

  useEffect(() => {
    filterPackage(rows, search);
  }, [rows, search]);

  useEffect(() => {
    setIsLoading(true);
    GetSumPackagesForAgent().then((res) => {
      if (res && res.status === 200) {
        setRows(res.data);
        setIsLoading(false);
      }
    });
  }, []);

  const sendWhatsappMessage = (message, dataObj, rows) => {
    setIsSending(true);
    const trackingNumbers = (dataObj.packageDetail || []).map((pkg) => pkg.tracking);

    enqueueAgentNotification(
      dataObj.agent.id,
      dataObj._id.station,
      message,
      trackingNumbers
    )
      .then((res) => {
        if (res && res.success && res.data?.status === "queued") {
          playCorrectSound();
          const rowId =
            res.data?.removeKeys?.rowKey ||
            dataObj.agent.id + dataObj._id.station;
          deleteRow(rows, rowId);
          Swal.fire({
            text: "Queued for sending",
            icon: "success",
            position: "top-right",
            timer: 1200,
            toast: true,
            showConfirmButton: false,
          });
        } else {
          playIncorrectSound();
          Swal.fire({
            title: "Error",
            icon: "error",
            text: res?.message || "Failed to queue message",
            timer: 3000,
          });
        }
      })
      .catch((error) => {
        console.error("Error queueing WhatsApp message:", error);
        playIncorrectSound();
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const updatePackage = (rows, dataObj) => {
    playAlertSound();
    Swal.fire({
      title: `ສົ່ງຂໍ້ຄວາມແຈ້ງ ${dataObj.agent.name} ແລ້ວບໍ?`,
      text: `Have you notified ${dataObj.agent.name} yet?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: " Yes ",
      cancelButtonText: "Not yet",
    }).then((result) => {
      if (result.isConfirmed) {
        const packageDetail = dataObj.packageDetail;
        const whatsappStatus = false;
        const paymentStatus = false;
        const agentWhatsappStatus = true;
        
        // Create array of promises to track completion properly
        const updatePromises = packageDetail.map((pkg) => 
          PatchPackageAgentWhatsappStatus(
            pkg._id,
            pkg.tracking,
            agentWhatsappStatus,
            whatsappStatus,
            paymentStatus
          ).then((res) => {
            if (!res.success) {
              console.log(res);
              throw new Error(res.message || 'Package update failed');
            }
            return res;
          })
        );

        // Wait for all packages to be updated, then show success once
        Promise.all(updatePromises)
          .then((results) => {
            console.log(`All ${packageDetail.length} packages have been updated successfully`);
            playCorrectSound(); // Play sound only once
            Swal.fire({
              title: "Success",
              text: `All ${packageDetail.length} packages have been updated successfully`,
              icon: "success",
              position: "top-right",
              timer: 2000,
              toast: true,
              showConfirmButton: false,
            });
            console.log(`Packages has been updated`);
            var rowId = dataObj.agent.id + dataObj._id.station;
            deleteRow(rows, rowId);
          })
          .catch((error) => {
            console.error('Error updating packages:', error);
            playIncorrectSound();
            Swal.fire({
              title: "Error",
              icon: "error",
              text: error.message || "Failed to update some packages",
              timer: 3000,
            });
          });
      }
    });
  };

  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 1,
      valueGetter: (params) => params.row.agent.id + params.row._id.station,
      hide: true,
    },
    {
      field: "stationId",
      headerName: "Station",
      flex: 0.5,
      valueGetter: (params) => params.row._id.station,
      hide: isMobile,
    },
    {
      field: "agentId",
      headerName: "Agent ID",
      flex: 0.5,
      valueGetter: (params) => params.row.agent.id,
      hide: false,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      valueGetter: (params) => params.row.agent.name,
      hide: isMobile,
    },
    {
      field: "whatsapp",
      headerName: "Whatsapp",
      flex: 1,
      valueGetter: (params) => params.row.agent.whatsapp,
      hide: isMobile,
    },

    {
      field: "smsMessage",
      headerName: "Message",
      flex: 3,
      hide: isMobile,
    },
    {
      field: "totalPackages",
      headerName: "item(s).",
      flex: 0.5,
    },
    {
      field: "totalShippingFee",
      headerName: "Fee",
      flex: 0.5,
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      flex: 0.5,
    },
    {
      field: "copy",
      headerName: "Copy",
      flex: 0.5,
      hide: false,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            onClick={() => {
              copyTextToClipboard(cellValues.row.smsMessage);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <ContentCopyIcon />
          </IconButton>
        );
      },
    },
    {
      field: "send",
      headerName: "Send",
      flex: 0.5,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            disabled={isSending}
            onClick={() => {
              sendWhatsappMessage(
                cellValues.row.smsMessage,
                cellValues.row,
                rows
              );
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <SendIcon />
          </IconButton>
        );
      },
    },
    {
      field: "success",
      headerName: "Success",
      flex: 0.5,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            onClick={() => {
              updatePackage(filteredRows, cellValues.row);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <CheckIcon />
          </IconButton>
        );
      },
    },
  ];

  // Conditionally filter columns based on isMobile state
  const filteredColumns =
    _user.role === "agent"
      ? columns.filter((column) => column.field !== "send")
      : columns;

  return (
    <Box height="74vh">
      {/*  Data Grid  */}
      <StyledDataGrid
        loading={isLoading}
        getRowId={(row) => row._id.station + row.agent.id}
        rows={filteredRows || []}
        columns={filteredColumns}
        rowCount={filteredRows ? filteredRows.length : 0}
        rowsPerPageOptions={[20, 50, 100]}
        pagination
        page={page}
        pageSize={pageSize}
        paginationMode="client"
        sortingMode="client"
        filterMode="client"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        // onSortModelChange={(newSortModel) => setSort(...newSortModel)}
        components={{ Toolbar: DataGridCustomToolbarClientMode }}
        componentsProps={{
          toolbar: { search, setSearch },
        }}
        containerSx={{ height: "100%" }}
        autoHeight={false}
      />
    </Box>
  );
};

export default Agent;
